import React from 'react';
import { toast } from 'react-toastify';
import { isEqual } from 'lodash-es';
import {
    faSave,
    faCheckCircle,
    faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { FormPrompt } from '@s20/components/common/FormPrompt';
import { clone } from '@s20/common/util/object';
import { httpClient } from '@s20/services/httpClient';
import CommonContext, { ApiRoutes, AppNavPaths, ServiceResponseResult } from '../Common';
import {
    AppPageForm,
    FlexCenterRow,
    onFieldChange,
    onReactSelectChanged,
    SubHeading,
    ToastMessage,
    Input,
    NumericInput,
} from '../common/forms/FormElements';
import { handleFormSaveError } from '../common/forms/ValidationError';
import { BaseFormViewModel } from '../common/ViewModel';
import { SaveButton } from '../common/forms/SaveButton';
import { USStateWorkTypePerDiemGrid } from './USStateWorkTypePerDiemGrid';

class USState {
    id = null;
    name = '';
    abbreviation = '';
    perDiemRate = 0;
    workRate = 0;
    driverRate = 0;
    stipendRate = 0;
    passengerRate = 0;
    laborGroupCode = '';
    workTypePerDiemRates = [];
}

class USStateForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);

        this.formRef = React.createRef();

        this.state = {
            originalData: new USState(),
            usState: new USState(),
            contractUnionWorkTypes: [],
            ...new BaseFormViewModel(),
        };

        this.handlers.change = this.handlers.change.bind(this);
        this.handlers.select = this.handlers.select.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }

    componentDidUpdate(prevProps) {
        if (
            prevProps
            && this.props.match.params.id !== (prevProps.match.params ?? {}).id
        ) {
            this.populateState();
        }
    }

    onClearErrors = () => this.setState({ errors: {} });

    onSubmit = async () => {
        this.onClearErrors();
        this.setSaving(true);

        const usState = clone(this.state.usState);

        const response = await httpClient
            .put(ApiRoutes.USStates.update(usState.id), usState)
            .catch(this.handleSaveError);

        if (response) {
            if (response.result === ServiceResponseResult.Ok) {
                this.setState({
                    saving: false,
                    formValidated: false,
                    originalData: usState,
                });

                toast.success(
                    <ToastMessage
                        icon={faCheckCircle}
                        header="Save Successful"
                        message={`${usState.name} has been updated`}
                    />,
                );
            } else {
                toast.error(
                    <ToastMessage
                        icon={faExclamationTriangle}
                        header="There was a problem saving"
                        message={`${response.message}`}
                    />,
                );
            }
        }
        this.setSaving(false);
    };

    setSaving = (b) => this.setState({ saving: b });

    handleSaveError = (err) => handleFormSaveError(this, err);

    handlers = {
        change: onFieldChange,
        select: onReactSelectChanged,
    };

    async populateState() {
        const { id } = this.props.match.params;

        const [usState, contractUnionWorkTypes] = await Promise.all([
            httpClient.js(ApiRoutes.USStates.getById(id)),
            httpClient.js(ApiRoutes.typeAheads.contractUnionWorkTypes()),
        ]);

        this.setState({ usState, contractUnionWorkTypes, loading: false });
    }

    render() {
        const {
            loading,
            usState,
            contractUnionWorkTypes,
            saving,
            formValidated,
            errors,
            originalData,
        } = this.state;

        return (
            <>
                <FormPrompt
                    when={!saving && !isEqual(originalData, usState)}
                    message="You have unsaved changes. Are you sure you want to leave?"
                />
                <AppPageForm
                    formShown={this.context.formIsOpen}
                    formId="usState"
                    formRef={this.formRef}
                    onSubmit={this.onSubmit}
                    formHeadingIcon={faSave}
                    formHeading="Edit State"
                    formName="usState"
                    setIsValidated={(value) => {
                        this.setState({ formValidated: value });
                    }}
                    isValidated={formValidated}
                    saving={saving}
                    errors={errors}
                    loading={loading}
                    backUrl={AppNavPaths.USStates}
                    backLabel="Back to States"
                >
                    <SubHeading first>State Details</SubHeading>
                    <Input
                        id="name"
                        name="name"
                        label="State Name"
                        value={usState.name ?? ''}
                        readOnly
                        className="form-control-plaintext no-readonly-bg"
                    />

                    <Input
                        id="abbreviation"
                        name="abbreviation"
                        label="Abbreviation"
                        value={usState.abbreviation ?? ''}
                        readOnly
                        className="form-control-plaintext no-readonly-bg"
                    />

                    <NumericInput
                        label="Per-Diem Rate"
                        name="usState.perDiemRate"
                        onChange={this.handlers.change}
                        value={usState.perDiemRate || 0}
                        min={0}
                        required
                        step={0.01}
                        error="You must provide a per-diem rate"
                    />

                    <NumericInput
                        label="Non-Union/Shop Work Rate"
                        name="usState.workRate"
                        onChange={this.handlers.change}
                        value={usState.workRate || 0}
                        min={0}
                        required
                        step={0.01}
                        error="You must provide a shop rate"
                    />

                    <NumericInput
                        label="Stipend Rate"
                        name="usState.stipendRate"
                        onChange={this.handlers.change}
                        value={usState.stipendRate || 0}
                        min={0}
                        required
                        step={0.01}
                        error="You must provide a stipend rate"
                    />

                    {Boolean(this.context.tenant.tenantSettings.enableDriverPaySources) && (
                        <>
                            <NumericInput
                                label="Driver Rate"
                                name="usState.driverRate"
                                onChange={this.handlers.change}
                                value={usState.driverRate || 0}
                                min={0}
                                required
                                step={0.01}
                                error="You must provide a driver rate"
                            />

                            <NumericInput
                                label="Passenger Rate"
                                name="usState.passengerRate"
                                onChange={this.handlers.change}
                                value={usState.passengerRate || 0}
                                min={0}
                                required
                                step={0.01}
                                error="You must provide a passenger rate"
                            />
                        </>
                    )}

                    {Boolean(this.context.tenant.tenantSettings.enablePrevailingWageContracts || this.context.tenant.tenantSettings.enforceContractUnionWork) && (
                        <Input
                            label="Labor Group Code for Certified Payroll"
                            name="usState.laborGroupCode"
                            onChange={this.handlers.change}
                            value={usState.laborGroupCode || ''}
                        />
                    )}

                    <SubHeading>Per-Work-Type Per Diem Overrides</SubHeading>
                    <USStateWorkTypePerDiemGrid
                        workTypes={contractUnionWorkTypes}
                        perDiemRates={usState.workTypePerDiemRates ?? []}
                        onPerDiemRatesChange={(rates) => this.setState((prev) => ({
                            usState: { ...prev.usState, workTypePerDiemRates: rates },
                        }))}
                    />

                    <FlexCenterRow className="mt-3 mb-4">
                        <SaveButton
                            saving={saving}
                            label="Update State"
                        />
                    </FlexCenterRow>
                </AppPageForm>
            </>
        );
    }
}
export default USStateForm;
